import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, ClipboardList, CircleCheck, Upload, Wallet } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CollapsibleInsights } from '@/components/shared/collapsible-insights';
import { ExpenseCategoriesSummaryPanel } from '@/components/spending/expense-categories-summary-panel';
import { ExpenseExternalPartiesPanel } from '@/components/spending/expense-external-parties-panel';
import { ExpenseDetailDialog } from '@/components/spending/expense-detail-dialog';
import { RecordExpenseSaveOptions } from '@/components/spending/record-expense-save-options';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import {
  DateRangeFilter,
  INSIGHTS_DATE_RANGE_PRESETS,
  LIST_FILTER_DATE_RANGE_PRESETS,
  resolvePreset,
  type DateRange,
} from '@/components/shared/date-range-filter';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { MetricMoneyCards } from '@/components/shared/money-totals-stack';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { ExpenseAccountField } from '@/components/spending/expense-account-field';
import { ExpenseCategorySelect } from '@/components/shared/expense-category-select';
import { ExpenseExternalPartySelect } from '@/components/shared/expense-external-party-select';
import { FilterBar } from '@/components/shared/filter-bar';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { SearchInput } from '@/components/shared/search-input';
import { StatCard } from '@/components/shared/stat-card';
import { Tip } from '@/components/shared/tip';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { MoneyInput } from '@/components/ui/money-input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import {
  dataTableMoneyColumnTotals,
  formatInsightsMoneySummary,
  type ListMoneyTotals,
} from '@/lib/money-totals';
import {
  loadRecordExpensePreferences,
  persistRecordExpensePreferences,
} from '@/lib/record-expense-preferences';
import { useChurchStore } from '@/stores/church-store';
import { usePreferences } from '@/stores/preferences-store';
import { formatDate, formatMoney, formatNumber, formatPercent } from '@/utils/format';

import { ExpenseCategoryImportDialog } from './expense-category-import-dialog';

const STATUS_ANY = '__any_status__';
const CATEGORY_ANY = '__any_category__';
const FUND_ANY = '__any_fund__';

const EXPENSE_STATUSES = [
  'draft',
  'pending_approval',
  'approved',
  'rejected',
  'paid',
  'void',
] as const;

interface Expense {
  id: string;
  payee: string;
  amountMinor: number;
  currency: string;
  expenseDate: string;
  status: string;
}

interface Member {
  id: string;
  firstName: string;
  lastName: string;
}

type PayeeKind = 'internal' | 'external' | 'other';

interface ExpenseInsights {
  paidAmountMinor: number;
  paidCount: number;
  currency: string;
  byCurrency?: Array<{ currency: string; amountMinor: number; count: number }>;
  comparison: {
    change: number;
    previousPaidAmountMinor: number;
    previousPaidCount: number;
  };
  pendingApprovalCount: number;
  approvedAwaitingPaymentCount: number;
  byCategory: Array<{
    categoryId: string;
    categoryName: string;
    amountMinor: number;
    count: number;
  }>;
}

const PAYEE_KINDS: PayeeKind[] = ['internal', 'external', 'other'];
type ExpensesTab = 'expenses' | 'byCategory' | 'companies';

export const ExpensesPage = () => {
  const { t } = useTranslation('spending');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const church = useChurchStore((state) => state.church);
  const membership = useChurchStore((state) => state.membership);
  const timezone = usePreferences((state) => state.timezone);
  const [tab, setTab] = useState<ExpensesTab>('expenses');
  const [createCategoryOpen, setCreateCategoryOpen] = useState(false);
  const [createPartyOpen, setCreatePartyOpen] = useState(false);
  const [importCategoriesOpen, setImportCategoriesOpen] = useState(false);
  const [insightsDateRange, setInsightsDateRange] = useState<DateRange>(() => ({
    preset: 'thisMonth',
    ...resolvePreset('thisMonth', timezone),
  }));
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState(STATUS_ANY);
  const [filterCategoryId, setFilterCategoryId] = useState(CATEGORY_ANY);
  const [filterFundId, setFilterFundId] = useState(FUND_ANY);
  const [filterDateRange, setFilterDateRange] = useState<DateRange>(() => ({
    preset: 'allTime',
    ...resolvePreset('allTime', timezone),
  }));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [open, setOpen] = useState(false);
  const [payeeKind, setPayeeKind] = useState<PayeeKind>('internal');
  const [payee, setPayee] = useState('');
  const [memberId, setMemberId] = useState<string | null>(null);
  const [memberQuery, setMemberQuery] = useState('');
  const [externalPartyId, setExternalPartyId] = useState<string | null>(null);
  const [externalPartyName, setExternalPartyName] = useState('');
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [accountCurrency, setAccountCurrency] = useState<string | null>(null);
  const [amountMinor, setAmountMinor] = useState<number | null>(null);
  const [expenseDate, setExpenseDate] = useState<string | null>(null);
  const [memo, setMemo] = useState('');
  const [savePreferences, setSavePreferences] = useState(() =>
    loadRecordExpensePreferences(church?.id),
  );
  const [formKey, setFormKey] = useState(0);
  const [actionTarget, setActionTarget] = useState<{
    row: Expense;
    action: 'reject' | 'approve' | 'pay';
  } | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkAction, setBulkAction] = useState<'reject' | 'approve' | 'pay' | null>(null);
  const [detailExpenseId, setDetailExpenseId] = useState<string | null>(null);

  const funds = useQuery({
    queryKey: ['giving', 'funds', 'active'],
    queryFn: () =>
      api.get<{ data: { id: string; name: string }[] }>(
        '/giving/funds?pageSize=100&activeOnly=true',
      ),
  });

  const categories = useQuery({
    queryKey: ['expenses', 'categories'],
    queryFn: () =>
      api.get<{ data: { id: string; name: string; isActive: boolean }[] }>(
        '/expenses/categories',
      ),
  });

  const list = useQuery({
    queryKey: [
      'expenses',
      page,
      pageSize,
      search,
      filterStatus,
      filterCategoryId,
      filterFundId,
      filterDateRange.from,
      filterDateRange.to,
    ],
    queryFn: () =>
      api.get<{
        data: Expense[];
        page: {
          page: number;
          pageSize: number;
          totalItems: number;
          totalPages: number;
        };
        totals?: ListMoneyTotals;
      }>('/expenses', {
        query: {
          page,
          pageSize,
          q: search.trim() || undefined,
          status: filterStatus === STATUS_ANY ? undefined : filterStatus,
          categoryId: filterCategoryId === CATEGORY_ANY ? undefined : filterCategoryId,
          fundId: filterFundId === FUND_ANY ? undefined : filterFundId,
          dateFrom: filterDateRange.from ?? undefined,
          dateTo: filterDateRange.to ?? undefined,
        },
      }),
    placeholderData: keepPreviousData,
  });

  const insights = useQuery({
    queryKey: ['expenses', 'insights', insightsDateRange.from, insightsDateRange.to],
    queryFn: () =>
      api.get<ExpenseInsights>('/expenses/insights', {
        query: {
          dateFrom: insightsDateRange.from ?? undefined,
          dateTo: insightsDateRange.to ?? undefined,
        },
      }),
  });

  const members = useQuery({
    queryKey: ['people', 'members', 'picker', 'expense-payee', memberQuery],
    queryFn: () =>
      api.get<{ data: Member[] }>(
        `/people/members?pageSize=20${
          memberQuery ? `&q=${encodeURIComponent(memberQuery)}` : ''
        }`,
      ),
    enabled: open && payeeKind === 'internal',
    placeholderData: keepPreviousData,
  });

  const appliedCount = useMemo(() => {
    let count = 0;
    if (search.trim()) count += 1;
    if (filterStatus !== STATUS_ANY) count += 1;
    if (filterCategoryId !== CATEGORY_ANY) count += 1;
    if (filterFundId !== FUND_ANY) count += 1;
    if (filterDateRange.preset !== 'allTime') count += 1;
    return count;
  }, [filterCategoryId, filterDateRange.preset, filterFundId, filterStatus, search]);

  const currency = accountCurrency ?? church?.baseCurrency ?? 'GHS';

  const memberOptions = useMemo(
    () =>
      (members.data?.data ?? []).map((member) => ({
        id: member.id,
        label: `${member.firstName} ${member.lastName}`.trim(),
      })),
    [members.data?.data],
  );
  const selectedMember = memberOptions.find((option) => option.id === memberId) ?? null;

  const payeeReady =
    payeeKind === 'internal'
      ? Boolean(memberId)
      : payeeKind === 'external'
        ? Boolean(externalPartyId)
        : Boolean(payee.trim());

  const submitPayeeName =
    payeeKind === 'internal'
      ? (selectedMember?.label ?? '')
      : payeeKind === 'external'
        ? externalPartyName
        : payee.trim();

  const resetEntryFields = () => {
    setPayee('');
    setMemberId(null);
    setMemberQuery('');
    setExternalPartyId(null);
    setExternalPartyName('');
    setAmountMinor(null);
    setMemo('');
  };

  const resetForm = () => {
    setPayeeKind('internal');
    resetEntryFields();
    setSubmitConfirmOpen(false);
    setCategoryId(null);
    setAccountId(null);
    setAccountCurrency(null);
    setExpenseDate(null);
  };

  const create = useMutation({
    mutationFn: () =>
      api.post<Expense>('/expenses', {
        payeeKind,
        payee: payeeKind === 'other' ? payee.trim() : undefined,
        memberId: payeeKind === 'internal' ? memberId : undefined,
        externalPartyId: payeeKind === 'external' ? externalPartyId : undefined,
        categoryId,
        financialPositionItemId: accountId,
        amountMinor,
        currency,
        expenseDate,
        description: memo.trim() ? memo.trim() : null,
        submit: true,
      }),
    onSuccess: () => {
      toast.success(t('expenses.saved'));
      setSubmitConfirmOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['expenses'] });
      void queryClient.invalidateQueries({ queryKey: ['expenses', 'insights'] });
      void queryClient.invalidateQueries({ queryKey: ['financial-position'] });
      if (savePreferences.addAnother) {
        resetEntryFields();
        setFormKey((current) => current + 1);
        return;
      }
      setOpen(false);
      resetForm();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: string }) =>
      api.post<Expense>(`/expenses/${id}/${action}`, {}),
    onSuccess: (_result, variables) => {
      setActionTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['expenses'] });
      void queryClient.invalidateQueries({ queryKey: ['expenses', 'insights'] });
      if (variables.action === 'pay') {
        void queryClient.invalidateQueries({ queryKey: ['financial-position'] });
      }
      if (detailExpenseId) {
        void queryClient.invalidateQueries({ queryKey: ['expenses', detailExpenseId] });
      }
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns: DataTableColumn<Expense>[] = [
    {
      id: 'date',
      header: t('expenses.columns.date'),
      cell: (row) => formatDate(row.expenseDate),
    },
    {
      id: 'payee',
      header: t('expenses.columns.payee'),
      cell: (row) => row.payee,
    },
    {
      id: 'amount',
      header: t('expenses.columns.amount'),
      money: (row) => ({ amount: row.amountMinor, currency: row.currency }),
    },
    {
      id: 'status',
      header: t('expenses.columns.status'),
      cell: (row) => t(`expenses.status.${row.status}`, { defaultValue: row.status }),
    },
  ];

  const bulkAct = useMutation({
    mutationFn: async ({ ids, action }: { ids: string[]; action: 'reject' | 'approve' | 'pay' }) => {
      for (const id of ids) {
        await api.post<Expense>(`/expenses/${id}/${action}`, {});
      }
      return { count: ids.length, action };
    },
    onSuccess: ({ count, action }) => {
      setBulkAction(null);
      setSelectedIds(new Set());
      toast.success(t(`expenses.bulk.${action}Done`, { count }));
      void queryClient.invalidateQueries({ queryKey: ['expenses'] });
      void queryClient.invalidateQueries({ queryKey: ['expenses', 'insights'] });
      if (action === 'pay') {
        void queryClient.invalidateQueries({ queryKey: ['financial-position'] });
      }
    },
    onError: (err) => {
      void queryClient.invalidateQueries({ queryKey: ['expenses'] });
      void queryClient.invalidateQueries({ queryKey: ['expenses', 'insights'] });
      void queryClient.invalidateQueries({ queryKey: ['financial-position'] });
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const rows = list.data?.data ?? [];
  const expenseNeedsAction = (row: Expense) =>
    row.status === 'pending_approval' || row.status === 'approved';
  const selectedRows = rows.filter((row) => selectedIds.has(row.id));
  const idleRows = selectedRows.filter((row) => !expenseNeedsAction(row));
  const payableRows = selectedRows.filter((row) => row.status === 'approved');
  const reviewableRows = selectedRows.filter((row) => row.status === 'pending_approval');
  const canSubmit =
    payeeReady &&
    Boolean(categoryId) &&
    Boolean(accountId) &&
    amountMinor != null &&
    Boolean(expenseDate);
  const canManageCategories = hasPermission(membership, PERMISSIONS.EXPENSES_MANAGE_CATEGORIES);
  const canCreateExpenses = hasPermission(membership, PERMISSIONS.EXPENSES_CREATE);
  const canApproveExpenses = hasPermission(membership, PERMISSIONS.EXPENSES_APPROVE);

  const detailExpense =
    detailExpenseId != null ? (rows.find((row) => row.id === detailExpenseId) ?? null) : null;

  const openWorkflowAction = (action: 'reject' | 'approve' | 'pay') => {
    const row = detailExpense ?? actionTarget?.row;
    if (!row) return;
    setActionTarget({ row, action });
  };

  let headerActions = null;
  if (tab === 'expenses' && canCreateExpenses) {
    headerActions = <Button onClick={() => setOpen(true)}>{t('expenses.create')}</Button>;
  } else if (tab === 'byCategory' && canManageCategories) {
    headerActions = (
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          leadingIcon={<Upload className="size-4" aria-hidden />}
          aria-label={t('categories.import.action')}
          onClick={() => setImportCategoriesOpen(true)}
        >
          {t('categories.import.action')}
        </Button>
        <Button onClick={() => setCreateCategoryOpen(true)}>{t('categories.add')}</Button>
      </div>
    );
  } else if (tab === 'companies' && canCreateExpenses) {
    headerActions = (
      <Button onClick={() => setCreatePartyOpen(true)}>{t('externalParties.add')}</Button>
    );
  }

  return (
    <div>
      <PageHeader
        title={t('expenses.title')}
        description={t('expenses.description')}
        tipId="spending.expensesVsBudgets"
        headerActions={headerActions}
      />

      <Tabs value={tab} onValueChange={(value) => setTab(value as ExpensesTab)}>
        <TabsList>
          <TabsTrigger value="expenses">{t('tabs.expenses')}</TabsTrigger>
          <TabsTrigger value="byCategory">{t('tabs.byCategory')}</TabsTrigger>
          <TabsTrigger value="companies">{t('tabs.companies')}</TabsTrigger>
        </TabsList>

        <TabsContent value="expenses" className="space-y-3">
      <CollapsibleInsights
        summary={[
          {
            label: t('expenses.insights.paid'),
            value: insights.data
              ? formatInsightsMoneySummary(
                  {
                    amountMinor: insights.data.paidAmountMinor,
                    currency: insights.data.currency,
                    count: insights.data.paidCount,
                    byCurrency: insights.data.byCurrency,
                  },
                  church?.baseCurrency ?? 'GHS',
                )
              : '…',
          },
          {
            label: t('expenses.insights.pendingApproval'),
            value: insights.data ? formatNumber(insights.data.pendingApprovalCount) : '…',
          },
        ]}
      >
        <DateRangeFilter
          value={insightsDateRange}
          onChange={setInsightsDateRange}
          labels={{
            today: tCommon('date.today'),
            yesterday: tCommon('date.yesterday'),
            thisWeek: tCommon('date.thisWeek'),
            lastWeek: tCommon('date.lastWeek'),
            thisMonth: tCommon('date.thisMonth'),
            lastMonth: tCommon('date.lastMonth'),
            thisQuarter: tCommon('date.thisQuarter'),
            thisYear: tCommon('date.thisYear'),
            lastYear: tCommon('date.lastYear'),
            allTime: tCommon('date.allTime'),
            custom: tCommon('date.custom'),
            from: t('expenses.insights.dateFrom'),
            to: t('expenses.insights.dateTo'),
          }}
          presets={INSIGHTS_DATE_RANGE_PRESETS}
        />

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {insights.data?.byCurrency && insights.data.byCurrency.length > 1 ? (
            <MetricMoneyCards
              label={t('expenses.insights.paid')}
              items={insights.data.byCurrency.map((item) => ({
                currency: item.currency,
                amountMinor: item.amountMinor,
              }))}
              loading={insights.isLoading}
            />
          ) : (
            <StatCard
              label={t('expenses.insights.paid')}
              money={
                insights.data
                  ? { amount: insights.data.paidAmountMinor, currency: insights.data.currency }
                  : undefined
              }
              icon={<Wallet className="size-4" aria-hidden />}
              loading={insights.isLoading}
              comparison={
                insights.data
                  ? {
                      change: insights.data.comparison.change,
                      label: t('expenses.insights.vsPreviousPeriod'),
                      increaseIsGood: false,
                      formatted: (() => {
                        const change = insights.data.comparison.change;
                        const formatted = formatPercent(Math.abs(change));
                        if (change > 0) return `+${formatted}`;
                        if (change < 0) return `-${formatted}`;
                        return formatted;
                      })(),
                    }
                  : undefined
              }
            />
          )}
          <StatCard
            label={t('expenses.insights.paidCount')}
            value={insights.data ? formatNumber(insights.data.paidCount) : undefined}
            icon={<Banknote className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('expenses.insights.pendingApproval')}
            value={
              insights.data ? formatNumber(insights.data.pendingApprovalCount) : undefined
            }
            icon={<ClipboardList className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('expenses.insights.approvedAwaitingPayment')}
            value={
              insights.data
                ? formatNumber(insights.data.approvedAwaitingPaymentCount)
                : undefined
            }
            icon={<CircleCheck className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
        </div>

      </CollapsibleInsights>

      <FilterBar
        appliedCount={appliedCount}
        onClearAll={() => {
          setSearch('');
          setFilterStatus(STATUS_ANY);
          setFilterCategoryId(CATEGORY_ANY);
          setFilterFundId(FUND_ANY);
          setFilterDateRange({
            preset: 'allTime',
            ...resolvePreset('allTime', timezone),
          });
          setPage(1);
        }}
        labels={{
          filters: t('expenses.filters.title'),
          clearAll: t('expenses.filters.clearAll'),
          applied: (count) => t('expenses.filters.applied', { count }),
        }}
        leading={
          <SearchInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder={t('expenses.filters.search')}
            clearLabel={tCommon('search.clear')}
            className="max-w-none sm:w-56"
            aria-label={t('expenses.filters.search')}
          />
        }
      >
        {({ popoverPortal }) => (
          <>
            <div className="w-48 min-w-[11rem]">
              <Listbox
                value={filterStatus}
                onChange={(value) => {
                  setFilterStatus(value ?? STATUS_ANY);
                  setPage(1);
                }}
                options={[
                  { value: STATUS_ANY, label: t('expenses.filters.statusAny') },
                  ...EXPENSE_STATUSES.map((status) => ({
                    value: status,
                    label: t(`expenses.status.${status}`),
                  })),
                ]}
                popoverPortal={popoverPortal}
              />
            </div>
            <div className="w-48 min-w-[11rem]">
              <Listbox
                value={filterCategoryId}
                onChange={(value) => {
                  setFilterCategoryId(value ?? CATEGORY_ANY);
                  setPage(1);
                }}
                options={[
                  { value: CATEGORY_ANY, label: t('expenses.filters.categoryAny') },
                  ...(categories.data?.data ?? [])
                    .filter((row) => row.isActive !== false)
                    .map((row) => ({
                      value: row.id,
                      label: row.name,
                    })),
                ]}
                popoverPortal={popoverPortal}
              />
            </div>
            <div className="w-48 min-w-[11rem]">
              <Listbox
                value={filterFundId}
                onChange={(value) => {
                  setFilterFundId(value ?? FUND_ANY);
                  setPage(1);
                }}
                options={[
                  { value: FUND_ANY, label: t('expenses.filters.fundAny') },
                  ...(funds.data?.data ?? []).map((fund) => ({
                    value: fund.id,
                    label: fund.name,
                  })),
                ]}
                popoverPortal={popoverPortal}
              />
            </div>
            <DateRangeFilter
              value={filterDateRange}
              onChange={(range) => {
                setFilterDateRange(range);
                setPage(1);
              }}
              popoverPortal={popoverPortal}
              labels={{
                today: tCommon('date.today'),
                yesterday: tCommon('date.yesterday'),
                thisWeek: tCommon('date.thisWeek'),
                lastWeek: tCommon('date.lastWeek'),
                thisMonth: tCommon('date.thisMonth'),
                lastMonth: tCommon('date.lastMonth'),
                thisQuarter: tCommon('date.thisQuarter'),
                thisYear: tCommon('date.thisYear'),
                lastYear: tCommon('date.lastYear'),
                allTime: tCommon('date.allTime'),
                custom: tCommon('date.custom'),
                from: t('expenses.filters.dateFrom'),
                to: t('expenses.filters.dateTo'),
              }}
              presets={LIST_FILTER_DATE_RANGE_PRESETS}
            />
          </>
        )}
      </FilterBar>

      {canApproveExpenses && selectedRows.length > 0 ? (
        <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
          <span className="text-body-sm text-fg-secondary">
            {t('expenses.bulk.selected', { count: selectedRows.length })}
          </span>
          {idleRows.length > 0 ? (
            <span className="text-body-sm text-fg-secondary">
              {t('expenses.bulk.noAction', { count: idleRows.length })}
            </span>
          ) : null}
          <Button size="sm" variant="secondary" onClick={() => setSelectedIds(new Set())}>
            {tCommon('action.clearSelection')}
          </Button>
          {reviewableRows.length > 0 ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setBulkAction('reject')}
            >
              {t('expenses.bulk.reject', { count: reviewableRows.length })}
            </Button>
          ) : null}
          {reviewableRows.length > 0 ? (
            <Button size="sm" variant="outline" onClick={() => setBulkAction('approve')}>
              {t('expenses.bulk.approve', { count: reviewableRows.length })}
            </Button>
          ) : null}
          {payableRows.length > 0 ? (
            <Button size="sm" onClick={() => setBulkAction('pay')}>
              {t('expenses.bulk.pay', { count: payableRows.length })}
            </Button>
          ) : null}
        </div>
      ) : null}

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        onRowClick={(row) => setDetailExpenseId(row.id)}
        selection={
          canApproveExpenses
            ? {
                selected: selectedIds,
                onChange: setSelectedIds,
                selectAllLabel: t('expenses.bulk.selectAll'),
                selectRowLabel: t('expenses.bulk.selectRow'),
                canSelect: expenseNeedsAction,
              }
            : undefined
        }
        totals={dataTableMoneyColumnTotals(
          'amount',
          list.data?.totals,
          church?.baseCurrency ?? 'GHS',
        )}
        empty={
          <p className="text-body-sm text-fg-secondary">
            {appliedCount > 0 ? t('expenses.emptyFiltered') : t('expenses.empty')}
          </p>
        }
        rowActions={(row) => (
          <div className="flex justify-end gap-2">
            {row.status === 'pending_approval' && canApproveExpenses ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(event) => {
                    event.stopPropagation();
                    setActionTarget({ row, action: 'reject' });
                  }}
                >
                  {t('expenses.reject')}
                </Button>
                <Button
                  size="sm"
                  onClick={(event) => {
                    event.stopPropagation();
                    setActionTarget({ row, action: 'approve' });
                  }}
                >
                  {t('expenses.approve')}
                </Button>
              </>
            ) : null}
            {row.status === 'approved' && canApproveExpenses ? (
              <Button
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  setActionTarget({ row, action: 'pay' });
                }}
              >
                {t('expenses.pay')}
              </Button>
            ) : null}
          </div>
        )}
        actionsHeader={canApproveExpenses ? tCommon('table.actions') : undefined}
      />

      {list.data?.page && list.data.page.totalPages > 1 ? (
        <Pagination
          page={list.data.page.page}
          pageSize={list.data.page.pageSize}
          totalItems={list.data.page.totalItems}
          totalPages={list.data.page.totalPages}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
          labels={{
            showing: ({ from, to, total }) => tCommon('pagination.showing', { from, to, total }),
            perPage: tCommon('pagination.perPage'),
            first: tCommon('pagination.first'),
            previous: tCommon('pagination.previous'),
            next: tCommon('pagination.next'),
            last: tCommon('pagination.last'),
          }}
        />
      ) : null}
        </TabsContent>

        <TabsContent value="byCategory">
          <ExpenseCategoriesSummaryPanel
            createOpen={createCategoryOpen}
            onCreateOpenChange={setCreateCategoryOpen}
          />
        </TabsContent>

        <TabsContent value="companies">
          <ExpenseExternalPartiesPanel
            createOpen={createPartyOpen}
            onCreateOpenChange={setCreatePartyOpen}
          />
        </TabsContent>
      </Tabs>

      <ExpenseDetailDialog
        expenseId={detailExpenseId}
        open={Boolean(detailExpenseId)}
        onOpenChange={(next) => {
          if (!next) setDetailExpenseId(null);
        }}
        canApprove={canApproveExpenses}
        onWorkflowAction={openWorkflowAction}
      />

      <ConfirmDialog
        open={submitConfirmOpen}
        onOpenChange={setSubmitConfirmOpen}
        title={t('confirm.submitExpense.title', {
          name: submitPayeeName,
          amount:
            amountMinor != null ? formatMoney(amountMinor, currency) : formatMoney(0, currency),
        })}
        description={t('confirm.submitExpense.description')}
        confirmLabel={t('confirm.submitExpense.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={create.isPending}
        onConfirm={() => {
          if (!canSubmit) return;
          create.mutate();
        }}
      />
      <ConfirmDialog
        open={actionTarget?.action === 'reject'}
        onOpenChange={(next) => {
          if (!next) setActionTarget(null);
        }}
        title={t('confirm.rejectExpense.title', { name: actionTarget?.row.payee ?? '' })}
        description={t('confirm.rejectExpense.description')}
        confirmLabel={t('confirm.rejectExpense.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={act.isPending}
        onConfirm={() => {
          if (actionTarget) act.mutate({ id: actionTarget.row.id, action: 'reject' });
        }}
      />
      <ConfirmDialog
        open={actionTarget?.action === 'approve'}
        onOpenChange={(next) => {
          if (!next) setActionTarget(null);
        }}
        title={t('confirm.approveExpense.title', { name: actionTarget?.row.payee ?? '' })}
        description={t('confirm.approveExpense.description')}
        confirmLabel={t('confirm.approveExpense.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={act.isPending}
        onConfirm={() => {
          if (actionTarget) act.mutate({ id: actionTarget.row.id, action: 'approve' });
        }}
      />
      <ConfirmDialog
        open={actionTarget?.action === 'pay'}
        onOpenChange={(next) => {
          if (!next) setActionTarget(null);
        }}
        title={t('confirm.payExpense.title', { name: actionTarget?.row.payee ?? '' })}
        description={t('confirm.payExpense.description')}
        confirmLabel={t('confirm.payExpense.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={act.isPending}
        onConfirm={() => {
          if (actionTarget) act.mutate({ id: actionTarget.row.id, action: 'pay' });
        }}
      />
      <ConfirmDialog
        open={bulkAction === 'reject'}
        onOpenChange={(next) => {
          if (!next) setBulkAction(null);
        }}
        title={t('confirm.bulkRejectExpense.title', { count: reviewableRows.length })}
        description={t('confirm.bulkRejectExpense.description')}
        confirmLabel={t('confirm.bulkRejectExpense.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={bulkAct.isPending}
        onConfirm={() => {
          bulkAct.mutate({ ids: reviewableRows.map((row) => row.id), action: 'reject' });
        }}
      />
      <ConfirmDialog
        open={bulkAction === 'approve'}
        onOpenChange={(next) => {
          if (!next) setBulkAction(null);
        }}
        title={t('confirm.bulkApproveExpense.title', { count: reviewableRows.length })}
        description={t('confirm.bulkApproveExpense.description')}
        confirmLabel={t('confirm.bulkApproveExpense.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={bulkAct.isPending}
        onConfirm={() => {
          bulkAct.mutate({ ids: reviewableRows.map((row) => row.id), action: 'approve' });
        }}
      />
      <ConfirmDialog
        open={bulkAction === 'pay'}
        onOpenChange={(next) => {
          if (!next) setBulkAction(null);
        }}
        title={t('confirm.bulkPayExpense.title', { count: payableRows.length })}
        description={t('confirm.bulkPayExpense.description')}
        confirmLabel={t('confirm.bulkPayExpense.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={bulkAct.isPending}
        onConfirm={() => {
          bulkAct.mutate({ ids: payableRows.map((row) => row.id), action: 'pay' });
        }}
      />

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) resetForm();
        }}
      >
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <form
            key={formKey}
            className="min-h-0"
            onSubmit={(event) => {
              event.preventDefault();
              if (!canSubmit) return;
              setSubmitConfirmOpen(true);
            }}
          >
            <DialogBody
              className="max-h-[calc(92dvh-3rem)]"
              contentClassName="flex flex-col gap-4"
            >
              <DialogHeader>
                <DialogTitle>{t('expenses.create')}</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col gap-1.5">
                <Label>{t('expenses.payeeType')}</Label>
                <Listbox
                  value={payeeKind}
                  onChange={(next) => {
                    const kind = (next as PayeeKind) ?? 'internal';
                    setPayeeKind(kind);
                    setPayee('');
                    setMemberId(null);
                    setMemberQuery('');
                    setExternalPartyId(null);
                    setExternalPartyName('');
                  }}
                  options={PAYEE_KINDS.map((kind) => ({
                    value: kind,
                    label: t(`expenses.payeeKinds.${kind}`),
                  }))}
                  popoverPortal="dialog"
                />
              </div>
              {payeeKind === 'internal' ? (
                <div className="flex flex-col gap-1.5">
                  <Label>{t('expenses.member')}</Label>
                  <EntityCombobox
                    value={memberId}
                    onChange={setMemberId}
                    options={memberOptions}
                    selected={selectedMember}
                    onQueryChange={setMemberQuery}
                    loading={members.isPending && !members.data}
                    popoverPortal="dialog"
                    clearLabel={tCommon('action.clearSelection')}
                    placeholder={t('expenses.memberPlaceholder')}
                  />
                </div>
              ) : null}
              {payeeKind === 'external' ? (
                <div className="flex flex-col gap-1.5">
                  <Label>{t('expenses.externalParty')}</Label>
                  <ExpenseExternalPartySelect
                    value={externalPartyId}
                    onChange={(id, party) => {
                      setExternalPartyId(id);
                      setExternalPartyName(party?.name ?? '');
                    }}
                    popoverPortal="dialog"
                  />
                </div>
              ) : null}
              {payeeKind === 'other' ? (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="expense-payee-other">{t('expenses.payee')}</Label>
                  <Input
                    id="expense-payee-other"
                    value={payee}
                    onChange={(event) => setPayee(event.target.value)}
                    placeholder={t('expenses.payeeOtherPlaceholder')}
                    required
                  />
                </div>
              ) : null}
              <div className="flex flex-col gap-1.5">
                <Label>{t('expenses.category')}</Label>
                <ExpenseCategorySelect
                  value={categoryId}
                  onChange={setCategoryId}
                  popoverPortal="dialog"
                />
              </div>
              <ExpenseAccountField
                open={open}
                value={accountId}
                onChange={(id, nextCurrency) => {
                  setAccountId(id);
                  setAccountCurrency(nextCurrency);
                }}
              />
              <div className="flex flex-col gap-1.5">
                <Label>{t('expenses.amount')}</Label>
                <MoneyInput value={amountMinor} onChange={setAmountMinor} currency={currency} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t('expenses.date')}</Label>
                <DatePicker value={expenseDate} onChange={setExpenseDate} popoverPortal="dialog" />
                <Tip tipId="spending.periodLock" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="expense-memo">{t('expenses.memo')}</Label>
                <Textarea
                  id="expense-memo"
                  value={memo}
                  onChange={(event) => setMemo(event.target.value)}
                  placeholder={t('expenses.memoPlaceholder')}
                  rows={2}
                />
              </div>
              <RecordExpenseSaveOptions
                addAnother={savePreferences.addAnother}
                onAddAnotherChange={(addAnother) => {
                  const next = { ...savePreferences, addAnother };
                  setSavePreferences(next);
                  persistRecordExpensePreferences(church?.id, next);
                }}
                disabled={create.isPending}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  {tCommon('action.cancel')}
                </Button>
                <Button type="submit" loading={create.isPending} disabled={!canSubmit}>
                  {t('expenses.submit')}
                </Button>
              </DialogFooter>
            </DialogBody>
          </form>
        </DialogContent>
      </Dialog>

      <ExpenseCategoryImportDialog
        open={importCategoriesOpen}
        onOpenChange={setImportCategoriesOpen}
      />
    </div>
  );
};
